import { Box, SxProps, useTheme, Typography, Chip, Tooltip } from "@mui/material";
import React, { FunctionComponent } from "react";

const OUTPUT_LABELS: Record<string, string> = {
  EXISTS: "Exists",
  LENGTH: "Length",
  COUNT: "Count",
  VALUE: "Value",
  COMPUTED_QUANTITY: "Computed Qty",
  QUANTITY: "Quantity",
  HOOKUP: "Hookup",
  VALUE_AREA: "Area",
};

const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  CATEGORY: { label: "Category", color: "#1a3a5c" },
  PARENT_CATEGORY: { label: "Parent Cat", color: "#2e5a8a" },
  SUBCATEGORY: { label: "Component", color: "#0277bd" },
  PARENT_SUBCATEGORY: { label: "Parent Comp", color: "#0288d1" },
  STATIC_SUBCATEGORY: { label: "Static Comp", color: "#00838f" },
  LINE: { label: "Fence", color: "#2e7d32" },
  POINT: { label: "Post", color: "#558b2f" },
  TAKEOFF_VARIABLE: { label: "Takeoff", color: "#6a1b9a" },
  SPECIFICATION: { label: "Spec", color: "#c62828" },
  NUMBER: { label: "Number", color: "#455a64" },
};

export const VariableButton: FunctionComponent<{
  line1?: string;
  line2?: string;
  onClick: () => void;
  output?: string;
  sx?: SxProps;
  fromParent?: boolean;
  typeLabel?: string;
  variableType?: string;
}> = ({ sx, line1, line2, output, onClick, fromParent, typeLabel, variableType }) => {
  const theme = useTheme();
  const outputDisplay = output ? (OUTPUT_LABELS[output] ?? output) : "";
  const typeInfo = variableType ? TYPE_LABELS[variableType] : null;
  const displayTypeLabel = typeLabel || typeInfo?.label || "";
  const bgColor = typeInfo?.color || (fromParent ? theme.palette.info.main : theme.palette.primary.main);
  
  const tooltipText = [
    displayTypeLabel && `Type: ${displayTypeLabel}`,
    line1 && `Name: ${line1}`,
    line2 && line2,
    outputDisplay && `Output: ${outputDisplay}`,
  ].filter(Boolean).join(" • ");

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Box
        onClick={onClick}
        sx={{
          margin: 0.5,
          minHeight: "32px",
          backgroundColor: bgColor,
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          borderRadius: "6px",
          padding: "4px 10px",
          gap: 0.75,
          ...(sx || {}),
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          transition: "transform 0.1s, box-shadow 0.1s",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
          },
        }}
      >
        {/* Type badge */}
        {displayTypeLabel && (
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "4px",
              px: 0.75,
              py: 0.25,
              fontSize: "9px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            {displayTypeLabel}
          </Box>
        )}
        
        {/* Main content */}
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          {line1 && (
            <Typography
              sx={{
                fontSize: line2 ? "11px" : "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.2,
              }}
            >
              {line1}
            </Typography>
          )}
          {line2 && (
            <Typography
              sx={{
                fontSize: "9px",
                opacity: 0.85,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.2,
              }}
            >
              {line2}
            </Typography>
          )}
        </Box>

        {/* Output badge */}
        {outputDisplay && (
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.25)",
              borderRadius: "4px",
              px: 0.75,
              py: 0.25,
              fontSize: "10px",
              fontWeight: 600,
              marginLeft: "auto",
            }}
          >
            → {outputDisplay}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};
